import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The 48px bar at the top of the pane, and how a screen fills it.           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE MOCKUP'S HEADER IS PER-SCREEN, AND OURS WAS ONE STATIC TITLE.** `markup.html`'s
 * `<header class="flex h-12 …">` branches seven ways: a plain title for most screens, but a
 * **dropdown** on the time section, a back button plus key plus title on a task, a back button plus
 * an editable name plus a kebab on the report builder, and a back button plus client plus a status
 * pill on a project. `app.tsx` rendered `t(route.title)` for every one of them, which is why the bar
 * looks nothing like the mockup on four screens.
 *
 * A screen cannot reach into the chrome that renders it, so this is the seam: the shell renders
 * whatever the current screen put here, and falls back to the route's own title when a screen puts
 * nothing.
 *
 * ⚠️ **A context rather than a prop through `routes.tsx`.** The header's contents depend on
 * *loaded data* — a project's name and status, a task's key — which the route table cannot know and
 * the screen does not have until its fetch resolves. Declaring it beside the path would mean either
 * a second fetch in the shell or a header that lags a navigation behind.
 *
 * ⚠️ **It renders in the shell's own DOM, not through a portal.** A portal would put the screen's
 * header outside the landmark structure `AppShell` builds and make its order in the accessibility
 * tree depend on mount timing.
 */

interface HeaderSlot {
  readonly node: ReactNode;
  readonly setNode: (node: ReactNode) => void;
}

const PageHeaderContext = createContext<HeaderSlot | null>(null);

export function PageHeaderProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [node, setNode] = useState<ReactNode>(null);
  const value = useMemo(() => ({ node, setNode }), [node]);
  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

/** What the shell should draw. `null` means "nobody claimed it" — use the route title. */
export function usePageHeaderNode(): ReactNode {
  return useContext(PageHeaderContext)?.node ?? null;
}

/**
 * Claim the header for as long as this screen is mounted.
 *
 * ⚠️⚠️ **IT CLEARS ON UNMOUNT, AND WITHOUT THAT THE HEADER LIES.** Navigating from a project to the
 * task list would otherwise leave the project's name, client and status pill sitting above a list of
 * every task in the workspace — a header describing a screen that is no longer there, which is worse
 * than a generic one because it looks authoritative.
 *
 * ⚠️⚠️ **`build` IS HELD IN A REF AND IS *NOT* A DEPENDENCY — AND THE FIRST VERSION FROZE THE WHOLE
 * APP BECAUSE OF IT.** The loop:
 *
 *  1. the effect runs and calls `setNode(...)`;
 *  2. that is state on the PROVIDER, so every child re-renders — including this screen;
 *  3. the screen re-creates its inline callbacks, so a dependency like `onBack` has a new identity;
 *  4. the effect re-runs. Go to 1.
 *
 * It pegs the main thread, so nothing responds: reported as *"no link works, I can't go to tasks,
 * can't go back, nothing updates"*. Nothing was broken about the links; the browser never got to
 * them.
 *
 * ⚠️ **So `deps` MUST BE VALUES THE HEADER DISPLAYS — strings, numbers, booleans — and never a
 * function or an object.** The builder closes over the latest callbacks through the ref, so they do
 * not need to be declared; declaring one is what caused this. `use-notifications.tsx` holds its
 * callback in a ref for the same reason, one layer over.
 */
export function usePageHeader(
  build: () => ReactNode,
  deps: readonly unknown[],
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ *NOT CLAIMING* THE HEADER IS DIFFERENT FROM CLAIMING IT AS EMPTY.│
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * An EMBEDDED screen — `TasksScreen` inside a project's Feladatok tab — used to call this with a
   * builder returning `null`, which is a claim: it wrote `null` into the slot and wiped the
   * project's header out from under itself. The reported sequence was exactly that. Open a project:
   * the header is right. Switch to Feladatok: the embedded screen mounts and blanks it. Switch
   * back: the project screen's effect does not re-run, because none of ITS dependencies changed —
   * so the header stays blank, with no name, no client, no status and no kebab.
   *
   * `false` makes the hook inert: it neither writes nor clears. The parent's claim stands, which is
   * the whole point of embedding.
   */
  enabled = true,
): void {
  const slot = useContext(PageHeaderContext);
  const setNode = slot?.setNode;

  /* ⚠️ Assigned during render rather than in an effect: the effect below may run before a
   * commit-phase update would land, and it must call the CURRENT closure. */
  const latest = useRef(build);
  latest.current = build;

  useEffect(() => {
    if (setNode === undefined || !enabled) return;
    setNode(latest.current());
    return () => {
      setNode(null);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `build` is deliberately excluded; it
       is a fresh closure every render and reaching it through `latest` is what stops the loop the
       header above describes. The caller declares the VALUES it renders. */
  }, [setNode, enabled, ...deps]);
}

/**
 * The mockup's plain title: **13.5px, medium**.
 *
 * ⚠️ `text-[13.5px] font-medium`, transcribed — ours was `text-[13px]`, which is the body size. Half
 * a pixel sounds like nothing and is the difference between a bar that reads as a heading and one
 * that reads as a line of text, because every other 13px thing on the screen is content.
 */
export function PlainTitle({ children }: { readonly children: ReactNode }): ReactNode {
  return <span className="truncate text-[13.5px] font-medium">{children}</span>;
}

/**
 * A list screen's header: title on the left, primary action on the right.
 *
 * ⚠️⚠️ **THE MOCKUP PUTS THE PRIMARY CTA IN THE HEADER, NOT IN THE LIST TOOLBAR.** `markup.html`
 * 196–232 branches the right-hand side of the 48px bar per screen — *Új feladat*, *Új ügyfél*, *Új
 * projekt*, *Meghívás* — each a `Button size="sm"` with a `plus` icon after a `flex-1` spacer. Ours
 * put them in `ListToolbar`'s `actions`, one row lower, mixed in with the filters.
 *
 * The difference is not cosmetic: in the header the action sits in the same place on every screen,
 * so "the thing that makes a new one" is one target people learn once. In the toolbar it moves with
 * the number of filters above the list.
 *
 * ⚠️ **The spacer is `flex-1`, matching the mockup** — the header is a flex row, so the CTA is
 * pushed right rather than positioned.
 */
export function ListHeader({
  title,
  action,
}: {
  readonly title: string;
  /** Omitted when the caller may not create — a disabled CTA would promise a permission. */
  readonly action?: ReactNode;
}): ReactNode {
  return (
    <>
      <PlainTitle>{title}</PlainTitle>
      <div className="flex-1" />
      {action}
    </>
  );
}
